import { NativeModules, Platform } from 'react-native';
import Constants from 'expo-constants';

/**
 * Single source of truth for where the backend lives.
 *
 * The host is resolved at RUNTIME, not baked into the bundle, because a hardcoded LAN IP in
 * `.env` goes stale the moment the router hands the dev machine a different DHCP lease — which
 * shows up on the device as an opaque `java.net.ConnectException: Failed to connect to
 * /192.168.x.x:3000` and costs an hour every time.
 *
 * How it resolves, in order:
 *
 *  1. `EXPO_PUBLIC_API_URL` pointing at a NON-local host (a real domain / deployed gateway) wins
 *     outright. That is the production/staging path and is never rewritten.
 *  2. Otherwise, in dev, the host is taken from the Metro dev server this bundle was downloaded
 *     from (`Constants.expoConfig.hostUri`). That address is reachable from the device by
 *     construction — the JS you are running arrived over it — so it is always the right answer,
 *     whatever the LAN IP happens to be today.
 *  3. Port and path come from `EXPO_PUBLIC_API_URL` if it was set, else from
 *     `EXPO_PUBLIC_API_PORT` (default 3000) and `/api` — the gateway's global prefix.
 *
 * Net effect: nothing in `.env` needs a host for local development, and a changing IP can never
 * break the app again.
 */

const DEFAULT_PORT = process.env.EXPO_PUBLIC_API_PORT?.trim() || '3000';
const DEFAULT_PATH = '/api';

/** Android's emulator reaches the host machine on this alias; `localhost` is the emulator itself. */
const ANDROID_EMULATOR_HOST = '10.0.2.2';

const LOOPBACK = /^(localhost|127\.\d{1,3}\.\d{1,3}\.\d{1,3}|\[?::1\]?)$/i;
const PRIVATE_LAN =
  /^(10\.\d{1,3}\.\d{1,3}\.\d{1,3}|192\.168\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3})$/;

const stripTrailingSlash = (url: string) => url.replace(/\/+$/, '');

/** True for anything that only means something on this machine or this Wi-Fi. */
function isLocalHost(host: string): boolean {
  return LOOPBACK.test(host) || PRIVATE_LAN.test(host) || host.toLowerCase().endsWith('.local');
}

interface UrlParts {
  scheme: string;
  host: string;
  port: string | null;
  path: string;
}

/**
 * Deliberately hand-rolled rather than using `new URL()`: Hermes' URL implementation has been
 * inconsistent about parsing bare `host:port` authorities across RN versions, and we need this to
 * work on the raw `hostUri` string too, which has no scheme at all.
 */
function parseUrl(input: string): UrlParts | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const schemeMatch = /^([a-z][a-z0-9+.-]*):\/\//i.exec(trimmed);
  const scheme = schemeMatch ? schemeMatch[1].toLowerCase() : 'http';
  const rest = schemeMatch ? trimmed.slice(schemeMatch[0].length) : trimmed;

  const slash = rest.indexOf('/');
  const authority = (slash === -1 ? rest : rest.slice(0, slash)).split('?')[0];
  const path = slash === -1 ? '' : rest.slice(slash).split('?')[0];
  if (!authority) return null;

  // IPv6 literals arrive bracketed (`[::1]:8081`); the colons inside must not be read as a port.
  if (authority.startsWith('[')) {
    const end = authority.indexOf(']');
    if (end === -1) return null;
    const port = authority.slice(end + 1).replace(/^:/, '');
    return { scheme, host: authority.slice(0, end + 1), port: port || null, path };
  }

  const [host, port] = authority.split(':');
  if (!host) return null;
  return { scheme, host, port: port || null, path };
}

/**
 * The URL this JS bundle was actually downloaded from, e.g.
 * `http://192.168.0.104:8081/index.bundle?platform=android`. React Native exposes it through the
 * SourceCode native module, which is populated by the native host at launch — so it is the
 * definitive answer to "which machine is serving me", available in Expo Go, in `expo run:android`
 * dev builds, and in bare RN alike. In a release build it is a `file://` path and we ignore it.
 *
 * Read through the public `NativeModules` surface rather than RN's internal getDevServer module,
 * and defensively, so a future RN reshuffle degrades to the manifest fallback below instead of
 * crashing the app at import time.
 */
function bundleScriptUrl(): string | null {
  try {
    const sourceCode = (NativeModules as unknown as Record<string, any>)?.SourceCode;
    const url = sourceCode?.getConstants?.()?.scriptURL ?? sourceCode?.scriptURL;
    if (typeof url !== 'string') return null;
    const lower = url.toLowerCase();
    return lower.startsWith('http://') || lower.startsWith('https://') ? url : null;
  } catch {
    return null;
  }
}

/**
 * The host of the machine serving this bundle — reachable from the device by construction, since
 * the JS you are reading arrived over it. Unlike `process.env.*`, which Metro inlines at bundle
 * time, this is read fresh at every launch, so a new DHCP lease is picked up automatically.
 */
function metroHost(): string | null {
  const c = Constants as unknown as {
    expoConfig?: { hostUri?: string | null } | null;
    expoGoConfig?: { debuggerHost?: string | null } | null;
    manifest2?: { extra?: { expoGo?: { debuggerHost?: string | null } | null } | null } | null;
  };

  const source =
    bundleScriptUrl() ??
    c.expoConfig?.hostUri ??
    c.expoGoConfig?.debuggerHost ??
    c.manifest2?.extra?.expoGo?.debuggerHost ??
    null;
  if (!source) return null;

  const parsed = parseUrl(source);
  if (!parsed) return null;

  // A bundle served from loopback means `adb reverse` is forwarding the device's localhost to this
  // machine. On a real phone over USB that is exactly right, so keep it. Only the emulator is sent
  // to its 10.0.2.2 alias, which works there without reversing the API port as well. (This used to
  // apply to every Android device, so a USB-connected phone called 10.0.2.2 and every request hung.)
  if (Platform.OS === 'android' && LOOPBACK.test(parsed.host) && isAndroidEmulator()) return ANDROID_EMULATOR_HOST;
  return parsed.host;
}

/** True on the Android emulator (its build fingerprint/model say so), false on a real phone. */
function isAndroidEmulator(): boolean {
  const c = (Platform.constants ?? {}) as { Fingerprint?: string; Model?: string; Brand?: string };
  return /generic|emulator|sdk_gphone|google_sdk/i.test(`${c.Fingerprint ?? ''} ${c.Model ?? ''} ${c.Brand ?? ''}`);
}

const DEFAULT_API_URL = 'https://salunbd-backend-service.onrender.com/api';

function resolveApiBaseUrl(): string {
  const configured = parseUrl(process.env.EXPO_PUBLIC_API_URL || DEFAULT_API_URL);

  // A real deployed backend — never second-guess it.
  if (configured && !isLocalHost(configured.host)) {
    return stripTrailingSlash(`${configured.scheme}://${configured.host}${configured.port ? `:${configured.port}` : ''}${configured.path}`);
  }

  const port = configured?.port ?? DEFAULT_PORT;
  const path = configured?.path || DEFAULT_PATH;
  const host = metroHost();

  if (host) return stripTrailingSlash(`http://${host}:${port}${path}`);

  // No dev server to ask (a production build with nothing configured, or a bundle running
  // detached). Keep whatever was configured rather than inventing a host.
  if (configured) {
    return stripTrailingSlash(`${configured.scheme}://${configured.host}${configured.port ? `:${configured.port}` : ''}${configured.path}`);
  }
  return DEFAULT_API_URL;
}

/** e.g. `http://192.168.0.104:3000/api` — no trailing slash. Empty only if truly unresolvable. */
export const API_BASE_URL = resolveApiBaseUrl();

/**
 * Auth endpoints default to the gateway's `/auth` route, but can be pointed elsewhere via
 * `EXPO_PUBLIC_AUTH_URL` if auth ever moves off the shared gateway. A local host there gets the
 * same runtime rewrite, so a stale IP in that variable cannot break login either.
 */
export const AUTH_BASE_URL = (() => {
  const configured = parseUrl(process.env.EXPO_PUBLIC_AUTH_URL ?? '');
  if (!configured) return API_BASE_URL ? `${API_BASE_URL}/auth` : '';
  if (!isLocalHost(configured.host)) {
    return stripTrailingSlash(`${configured.scheme}://${configured.host}${configured.port ? `:${configured.port}` : ''}${configured.path}`);
  }
  const host = metroHost() ?? configured.host;
  return stripTrailingSlash(`http://${host}:${configured.port ?? DEFAULT_PORT}${configured.path || `${DEFAULT_PATH}/auth`}`);
})();

export const isBackendConfigured = API_BASE_URL.length > 0;

if ((process.env.NODE_ENV !== 'production')) {
  // One line in the Metro log that answers "what is it actually calling?" instantly, so a
  // connection error never again needs a bisect to diagnose.
  console.log(`[api] base URL: ${API_BASE_URL || '(unresolved)'} (dev host: ${metroHost() ?? 'not detected'})`);
}
